/* eslint-disable @typescript-eslint/no-explicit-any -- ok */
/* eslint-disable complexity  -- complexity*/
import type { Dictionary, LogProvider } from '@vista/diagnostics'
import { ThresholdLevel } from '@vista/diagnostics'
import type { CmsLogLevel } from '../../../cms/types/cmsLogLevel'
import type { LoggerConfiguration } from '../logger.types'
import { getThresholdLevelFromLogLevel } from '../logger.types'

export abstract class BaseLogger implements LogProvider {
    public constructor() {}

    public log(
        configuration: Dictionary | LoggerConfiguration,
        debugByTagPool: string[], // Our implementation allows for enabling tag level debugs for any severity level via the cms. Ignore these values.
        severity: ThresholdLevel,
        method: string, // Method and severity provide the same type of information. Use severity for easy comparison with configured log levels.
        message: string,
        tags: string[],
        args: any[]
    ) {
        try {
            const loggerConfiguration = (configuration?.['config'] ?? []) as LoggerConfiguration

            const logLevel = this.getLogLevelForTags(loggerConfiguration, tags)

            if (severity < logLevel) {
                return
            }

            switch (severity) {
                case ThresholdLevel.TRACE:
                    this.trace(tags, message, args)
                    break
                case ThresholdLevel.DEBUG:
                    this.debug(tags, message, args)
                    break
                case ThresholdLevel.INFO:
                    this.info(tags, message, args)
                    break
                case ThresholdLevel.WARN:
                    this.warn(tags, message, args)
                    break
                case ThresholdLevel.ERROR:
                    this.error(tags, message, args)
                    break
                default:
                // Swallow any unknown or silent level logs
            }
        } catch (error) {
            if (!(error instanceof Error)) return

            // Must output to console log as we're inside the logger we'd normally use and it's just broken...
            // eslint-disable-next-line no-console -- we're logging to console
            console.error(error.message)
        }
    }

    public abstract trace(tags: string[], message: string, args: any[]): void
    public abstract debug(tags: string[], message: string, args: any[]): void
    public abstract info(tags: string[], message: string, args: any[]): void
    public abstract warn(tags: string[], message: string, args: any[]): void
    public abstract error(tags: string[], message: string, args: any[]): void

    protected abstract getLogLevel([configuration]: LoggerConfiguration): CmsLogLevel

    private getLogLevelForTags(configurationList: LoggerConfiguration, tags: string[]): ThresholdLevel {
        if (!configurationList || !Array.isArray(configurationList)) {
            return ThresholdLevel.SILENT
        }

        /**
         * Scan configuration in reverse order (bottom to top in Umbraco), use first matching config.
         * This allows for specific tags to be removed from more generic tags higher in the configuration stack
         *
         *   Example                            Config              Outcome
         *
         *   We want everything but GTM         .*  -> Debug        In this case, GTM will only log at an error level.
         *   to log at debug level              GTM -> Error        Everything else will log at debug level.
         *
         *   We only want GTM to log at trace,  .*  -> Warn         In this case, GTM will log at trace level.
         *   everything else at warning.        GTM -> Trace        Everything else will log at warn level.
         */
        for (const configuration of configurationList.reverse() ?? []) {
            const isMatch = tags.some(tag => new RegExp(configuration.tag).test(tag))
            const configuredLevel = this.getLogLevel([configuration])
            const thresholdLevel = getThresholdLevelFromLogLevel(configuredLevel)

            if (isMatch) {
                return thresholdLevel
            }
        }

        return ThresholdLevel.SILENT
    }
}
